import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, FileText, Trash2, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, fmt } from "@/lib/api";
import StatTiles from "@/components/usage/StatTiles";
import UsageCharts from "@/components/usage/UsageCharts";
import SizingCard from "@/components/sizing/SizingCard";
import PanelArray from "@/components/sizing/PanelArray";
import AiDiagnostic from "@/components/sizing/AiDiagnostic";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import FirmwarePairingCard from "@/components/devices/FirmwarePairingCard";
import { useRegion } from "@/context/RegionContext";

export default function DeviceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { regions } = useRegion();
  const [device, setDevice] = useState(null);
  const [data, setData] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [existingReport, setExistingReport] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, s, reports] = await Promise.all([api.get(`/devices/${id}`), api.get(`/devices/${id}/sizing`), api.get("/reports")]);
      setDevice(d.data); setData(s.data);
      setExistingReport(reports.data.find((r) => r.device_id === id) || null);
    } catch (e) { toast.error(errMsg(e)); nav("/"); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  const changeRegion = async (code) => {
    try {
      await api.patch(`/devices/${id}`, { region: code || "AUTO" });
      toast.success(code ? `Inventory switched to ${code}` : "Inventory set to auto (by country)");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doCreateReport = async () => {
    setDuplicateWarning(false);
    setCreating(true);
    try {
      const { data: rep } = await api.post("/reports", { device_id: id });
      toast.success("Report draft created");
      nav(`/reports/${rep.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setCreating(false); }
  };

  const handleGenerateClick = () => {
    if (existingReport) setDuplicateWarning(true);
    else doCreateReport();
  };

  const remove = async () => {
    try { await api.delete(`/devices/${id}`); toast.success("Device unlinked"); nav("/"); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!device || !data) return <div className="h-full bg-white flex items-center justify-center kl-label">Loading node…</div>;
  const { usage, sizing, explanation } = data;
  const online = device.status === "active";

  return (
    <div className="h-full overflow-y-auto bg-white text-kl-black kl-scroll-light" data-testid="device-detail-page">
      <div className="border-b border-kl-lineLight px-8 py-5 flex items-start justify-between gap-6 sticky top-0 bg-white z-10">
        <div className="flex items-start gap-5 min-w-0">
          <Link to="/" data-testid="back-to-map" className="mt-1 p-2 border border-kl-lineLight hover:bg-kl-black hover:text-white transition-colors duration-150"><ArrowLeft size={16} /></Link>
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${online ? "border-kl-active text-kl-greenDark" : "border-kl-inactive text-kl-inactive"}`} data-testid="device-status-badge">
                <span className={`w-1.5 h-1.5 ${online ? "bg-kl-active" : "bg-kl-inactive"}`} />{online ? "online" : "offline"}
              </span>
              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-kl-lineLight text-kl-mutedLight" title="Last time the board pushed a reading" data-testid="device-last-sync">
                <Clock size={11} /> Last sync {fmt.ago(device.last_seen)}
              </span>
              <span className="kl-label">{device.client_type}</span>
              <span className="font-mono text-xs text-kl-mutedLight" data-testid="device-serial">{device.serial}</span>
              <label className="inline-flex items-center gap-2 border border-kl-lineLight pl-2 h-6" title="Inventory & currency used for sizing">
                <span className="kl-label">{device.region_auto ? "auto" : "manual"}</span>
                <select data-testid="device-region-select" value={device.region_auto ? "" : device.region_code} onChange={(e) => changeRegion(e.target.value)} className="h-full bg-transparent font-mono text-[11px] pr-2 focus:outline-none cursor-pointer">
                  <option value="">{`Auto → ${device.region_name} (${device.currency})`}</option>
                  {regions.map((r) => <option key={r.code} value={r.code}>{`${r.name} · ${r.currency}`}</option>)}
                </select>
              </label>
            </div>
            <h1 className="font-display font-light text-3xl mt-1 truncate" data-testid="device-client-name">{device.client_name}</h1>
            <p className="text-sm text-kl-mutedLight mt-0.5">
              {[device.address, device.city, device.country].filter(Boolean).join(", ") || "No site assigned"} · SIM {device.sim_number || "—"} · {device.firmware}
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={handleGenerateClick} disabled={creating || !usage.days} data-testid="create-report-button" className="kl-btn-primary"><FileText size={15} /> {creating ? "Preparing…" : "Generate proposal"}</button>
          <button onClick={() => setRemoving(true)} data-testid="device-detail-remove-button" title="Unlink" className="kl-btn-outline px-3 hover:bg-kl-inactive hover:border-kl-inactive"><Trash2 size={15} /></button>
        </div>
      </div>

      <div className="px-8 pt-6">
        <FirmwarePairingCard device={device} onChange={load} />
      </div>

      {usage.days === 0 ? (
        <div className="m-8 border border-dashed border-kl-lineLight p-16 text-center" data-testid="no-data-state">
          <p className="kl-label">No telemetry yet</p>
          <h2 className="font-display text-2xl mt-2">Waiting for the first log</h2>
          <p className="text-sm text-kl-mutedLight mt-2 max-w-md mx-auto">Waiting for the device to push readings to <span className="font-mono">/api/ingest/{device.serial}</span>.</p>
        </div>
      ) : (
        <div className="p-8 grid grid-cols-12 gap-6">
          <div className="col-span-12"><StatTiles usage={usage} /></div>
          <div className="col-span-12 xl:col-span-8 space-y-6">
            <UsageCharts usage={usage} />
            <PanelArray sizing={sizing} />
            <AiDiagnostic deviceId={id} initial={explanation} />
          </div>
          <div className="col-span-12 xl:col-span-4 space-y-6">
            <SizingCard sizing={sizing} />
          </div>
        </div>
      )}

      <ConfirmDialog open={removing} onOpenChange={setRemoving} title="Unlink this device?" description={`${device.serial} — all readings will be deleted.`} confirmLabel="Unlink" onConfirm={remove} />

      <Dialog open={duplicateWarning} onOpenChange={setDuplicateWarning}>
        <DialogContent className="max-w-md rounded-none border-kl-black p-0 gap-0" data-testid="duplicate-proposal-dialog">
          <DialogHeader className="bg-kl-black text-white p-6">
            <p className="kl-label-dark flex items-center gap-1.5"><AlertTriangle size={12} className="text-yellow-400" /> Already generated</p>
            <DialogTitle className="font-display font-light text-2xl text-white">A proposal already exists</DialogTitle>
            <DialogDescription className="text-kl-muted">
              {device?.client_name} already has a draft proposal{existingReport?.status === "sent" ? " (sent)" : ""} — check it in Reports first. Regenerating creates a second, separate proposal for this device.
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 flex flex-col gap-2">
            <button onClick={() => nav(`/reports/${existingReport?.id}`)} className="kl-btn-primary w-full h-11" data-testid="duplicate-view-existing">
              <FileText size={15} /> Check existing proposal
            </button>
            <button onClick={doCreateReport} disabled={creating} className="kl-btn-outline w-full h-11" data-testid="duplicate-regenerate">
              {creating ? "Creating…" : "Regenerate anyway"}
            </button>
            <button onClick={() => setDuplicateWarning(false)} className="kl-btn-ghost w-full h-9" data-testid="duplicate-cancel">Cancel</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
